// Niveau 250 (arène libre seulement) : 30 classes de plus, deux par famille (choix de Léni, 5 novembre 2026).
//  - « Maître » (Cxx-M, niveau 100) : accessible depuis chacune des six classes terminales de la famille ;
//  - « Légende » (Cxx-L, niveau 175) : suite du Maître.
// Ajoutées après les 150 classes du dossier : les index réseau des classes existantes ne changent pas.
// Chaque kit garde le caractère de sa famille, avec un peu plus de débit qu'une terminale du niveau 60 (mesuré par
// scripts/balance-report.ts, docs/decisions.md). Les valeurs sont des propositions à équilibrer en jeu.
import type { RawClassNode } from './data.generated';
import { autoTurret, beam, drone, emitter, hatch, launcher, mine, missile, mortar, orb, shell, trap } from './kits-extra';
import { single as b, type ClassKit } from './weapons';

export const MASTER_LEVEL = 100;
export const LEGEND_LEVEL = 175;

export interface RawClassNode250 extends RawClassNode {
  /** Classes d'où l'on peut évoluer (toutes les terminales de la famille pour un Maître). */
  parents: string[];
}

const FAMILIES: [code: string, family: string][] = [
  ['C01', 'Assaut'],
  ['C02', 'Précision'],
  ['C03', 'Artillerie'],
  ['C04', 'Missiles'],
  ['C05', 'Lasers'],
  ['C06', 'Drones'],
  ['C07', 'Mines'],
  ['C08', 'Pièges'],
  ['C09', 'Collision'],
  ['C10', 'Dispersion'],
  ['C11', 'Rebond'],
  ['C12', 'Tourelles'],
  ['C13', 'Énergie'],
  ['C14', 'Contrôle'],
  ['C15', 'Hybrides'],
];

/** Nom et comportement (bénéfice, contrepartie), comme les fiches du dossier. */
const TEXT: Record<string, [string, string]> = {
  'C01-M': ['Hydre', 'six canons alternés en éventail, dispersion moyenne'],
  'C01-L': ['Titan', 'triple canon rotatif à cadence croissante, chauffe'],
  'C02-M': ['Faucon', 'deux tirs perçants à très longue portée, cadence faible'],
  'C02-L': ['Foudroyeur', 'rayon chargé qui traverse tout, charge longue'],
  'C03-M': ['Tonnerre', 'deux mortiers alternés aux impacts larges, recharge longue'],
  'C03-L': ['Apocalypse', 'obus géant à sous-munitions, avertissement visible'],
  'C04-M': ['Salve', 'quatre missiles guidés par tir, faible pénétration'],
  'C04-L': ['Ogive', 'gros missile guidé qui accélère, départ lent'],
  'C05-M': ['Prisme', 'trois rayons qui rebondissent une fois, chauffe'],
  'C05-L': ['Supernova', 'rayon continu qui chauffe sa cible, exige un suivi constant'],
  'C06-M': ['Essaim-mère', 'trois soutes et douze drones, dégâts par drone moyens'],
  'C06-L': ['Porte-avions', 'quatre soutes et seize drones, rayon de contrôle court'],
  'C07-M': ['Semeur', 'deux poseurs arrière, champ de mines dense'],
  'C07-L': ['Cataclysme', 'grosses mines à éclats qui se rapprochent, nombre limité'],
  'C08-M': ['Citadelle', 'trois tubes de pièges dont un piège tireur, portée limitée'],
  'C08-L': ['Dédale', 'pièges tireurs dans quatre directions, cadence lente'],
  'C09-M': ['Mastodonte', 'blindage frontal et élan, arrière vulnérable'],
  'C09-L': ['Cuirassé', 'corps énorme qui écrase tout en ligne droite, virages lents'],
  'C10-M': ['Tempête', 'trois gerbes en éventail, portée courte'],
  'C10-L': ['Déluge', 'double gerbe très large, temps mort long'],
  'C11-M': ['Kaléidoscope', 'trois tirs qui rebondissent deux fois, pertes à chaque rebond'],
  'C11-L': ['Écho', 'balles persistantes dans quatre directions, nombre limité'],
  'C12-M': ['Amiral', 'canon lourd et trois tourelles automatiques, rotation lente'],
  'C12-L': ['Léviathan', 'double canon lourd et quatre tourelles, très lent'],
  'C13-M': ['Tesla', 'deux arcs qui sautent entre les cibles, faible dégât isolé'],
  'C13-L': ['Quasar', 'orbe lent qui attire puis éclate, avertissement clair'],
  'C14-M': ['Blizzard', 'un tir qui ralentit, un tir qui repousse, dégâts modestes'],
  'C14-L': ['Trou noir', 'orbe qui aspire fort, ralentissement latéral, esquivable'],
  'C15-M': ['Arsenal', 'canon, deux drones et une mine arrière, puissance partagée'],
  'C15-L': ['Panthéon', 'laser, canon, mortier et drones à la fois, fragile'],
};

const TERMINALS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export const RAW_CLASSES_250: RawClassNode250[] = FAMILIES.flatMap(([code, family]) => {
  const m = `${code}-M`;
  const l = `${code}-L`;
  const terminals = TERMINALS.map((t) => `${code}-${t}`);
  return [
    { id: m, family, name: TEXT[m][0], desc: TEXT[m][1], parent: terminals[0], parents: terminals, level: MASTER_LEVEL },
    { id: l, family, name: TEXT[l][0], desc: TEXT[l][1], parent: m, parents: [m], level: LEGEND_LEVEL },
  ];
});

export const KITS_250: Record<string, ClassKit> = {
  // ── C01 Assaut ─────────────────────────────────────────────
  'C01-M': {
    weapon: {
      reload: 0.5,
      barrels: [-0.75, -0.45, -0.15, 0.15, 0.45, 0.75].map((o, i) =>
        b({ angle: o * 3, offset: o, width: 0.36, length: i % 2 ? 2.0 : 1.8, damage: 0.62, spread: 3, delay: [0, 0.5, 0.17, 0.67, 0.33, 0.83][i] }),
      ),
    },
  },
  'C01-L': {
    weapon: {
      reload: 0.45,
      ramp: { max: 2.0, perShot: 0.06, decay: 1.2 },
      heat: { perShot: 0.02, cool: 0.3, lockout: 0.35 },
      barrels: [-0.36, 0, 0.36].map((o, i) => b({ offset: o, length: i === 1 ? 2.3 : 2.05, width: 0.5, damage: 0.4, spread: 3, delay: i / 3 })),
    },
    body: { speed: 0.95 },
  },

  // ── C02 Précision ──────────────────────────────────────────
  'C02-M': {
    weapon: { reload: 1.0, moveWhileFiring: 0.6, barrels: [-0.24, 0.24].map((o, i) => b({ offset: o, length: 2.8, width: 0.48, damage: 1.4, pen: 2.8, speed: 2.1, life: 1.8, delay: i * 0.5 })) },
    body: { health: 0.9 },
  },
  'C02-L': {
    weapon: {
      reload: 0.6,
      charge: { time: 1.5, minFrac: 0.2, damageAt0: 1.3, damageAt1: 7.0, moveMul: 0.55 },
      barrels: [b({ length: 3.0, width: 0.7, pen: 5, speed: 4.4, life: 0.6, proj: beam() })],
    },
  },

  // ── C03 Artillerie ─────────────────────────────────────────
  'C03-M': {
    weapon: { reload: 2.2, barrels: [-0.4, 0.4].map((o, i) => mortar({ offset: o, width: 0.95, damage: 2.8, delay: i * 0.5, proj: shell(250, 1050, 120) })) },
    body: { speed: 0.9 },
  },
  'C03-L': {
    weapon: {
      reload: 3.6,
      barrels: [mortar({ width: 1.6, damage: 4.5, speed: 0.5, size: 1.5, proj: shell(350, 1100, 180, 1, { frags: { count: 8, spread: 360, damage: 0.5, speed: 0.7, life: 0.4 } }) })],
    },
    body: { speed: 0.85 },
  },

  // ── C04 Missiles ───────────────────────────────────────────
  'C04-M': {
    weapon: {
      reload: 1.3,
      barrels: [-0.4, 0.4].map((o, i) => launcher({ offset: o, width: 0.75, damage: 0.6, pellets: 2, spread: 12, delay: i * 0.5, proj: missile({ homing: 2.5, homingDelay: 0.15 }) })),
    },
  },
  'C04-L': {
    weapon: {
      reload: 1.8,
      barrels: [launcher({ width: 1.3, damage: 3.6, speed: 0.4, life: 2.6, size: 1.2, proj: missile({ accel: 1.4, maxSpeedMul: 2.6, homing: 3, homingDelay: 0.3, explode: { radius: 120, damage: 0.9, onHit: true } }) })],
    },
  },

  // ── C05 Lasers ─────────────────────────────────────────────
  'C05-M': {
    weapon: {
      reload: 0.32,
      heat: { perShot: 0.045, cool: 0.4, lockout: 0.3 },
      barrels: [-3, 0, 3].map((a, i) => emitter({ angle: a, width: 0.45, damage: 0.9, life: 0.3, delay: i * 0.02, proj: beam({ bounces: 1, bounceLoss: 0.4 }) })),
    },
  },
  'C05-L': {
    weapon: {
      reload: 0.1,
      moveWhileFiring: 0.6,
      heat: { perShot: 0.018, cool: 0.3, lockout: 0.35 },
      barrels: [emitter({ width: 0.62, damage: 0.32, life: 0.22, proj: beam({ stack: { per: 0.12, max: 2.2 } }) })],
    },
  },

  // ── C06 Drones ─────────────────────────────────────────────
  'C06-M': { weapon: { reload: 0.9, barrels: [90, -90, 180].map((a, i) => hatch(a, { damage: 0.75, delay: i / 3, proj: drone(4) })) } },
  'C06-L': {
    weapon: { reload: 0.9, barrels: [45, 135, -45, -135].map((a, i) => hatch(a, { damage: 0.85, delay: i / 4, proj: drone(4, { maxSpeedMul: 1.2, control: 550 }) })) },
  },

  // ── C07 Mines ──────────────────────────────────────────────
  'C07-M': {
    weapon: { reload: 1.1, barrels: [150, -150].map((a, i) => b({ look: 'mine', angle: a, length: 1.3, width: 0.8, speed: 0.45, damage: 2.2, pen: 0.6, delay: i * 0.5, proj: mine(6, 90, 1) })) },
  },
  'C07-L': {
    weapon: {
      reload: 1.8,
      barrels: [b({ look: 'mine', length: 1.5, width: 1.25, speed: 0.5, damage: 4, pen: 0.7, size: 1.2, proj: mine(5, 140, 1, { seek: 120, frags: { count: 8, spread: 360, damage: 0.6, speed: 0.8, life: 0.35 } }) })],
    },
  },

  // ── C08 Pièges ─────────────────────────────────────────────
  'C08-M': {
    weapon: {
      reload: 1.0,
      barrels: [
        b({ look: 'trap', angle: -35, length: 1.4, width: 0.8, speed: 0.85, damage: 1.0, pen: 2, life: 6, proj: trap(5) }),
        b({ look: 'trap', length: 1.5, width: 1.0, speed: 0.85, damage: 0.6, pen: 1.4, life: 7, delay: 0.33, proj: trap(5, { turret: { range: 380, damage: 2.2 } }) }),
        b({ look: 'trap', angle: 35, length: 1.4, width: 0.8, speed: 0.85, damage: 1.0, pen: 2, life: 6, delay: 0.67, proj: trap(5) }),
      ],
    },
  },
  'C08-L': {
    weapon: {
      reload: 1.6,
      barrels: [0, 90, 180, 270].map((a, i) => b({ look: 'trap', angle: a, length: 1.4, width: 0.95, speed: 0.75, damage: 0.6, pen: 1.8, life: 8, delay: i / 4, proj: trap(3, { turret: { range: 420, damage: 1.8 } }) })),
    },
  },

  // ── C09 Collision ──────────────────────────────────────────
  'C09-M': {
    weapon: { reload: 0.9, barrels: [b({ length: 1.4, width: 0.55, damage: 0.45 })] },
    body: { bodyDamage: 3.8, health: 1.7, speed: 0.9, frontArmor: { reduction: 0.45, rearMul: 1.3 }, momentum: { speed: 0.4, damage: 1.8, build: 0.6 } },
  },
  'C09-L': {
    weapon: { reload: 0.8, barrels: [0, 90, 180, 270].map((a) => b({ angle: a, length: 1.25, width: 0.4, damage: 0.3 })) },
    body: { bodyDamage: 3.4, health: 1.9, speed: 0.88, frontArmor: { reduction: 0.5, rearMul: 1.35 }, momentum: { speed: 0.6, damage: 1.9, build: 0.5 } },
  },

  // ── C10 Dispersion ─────────────────────────────────────────
  'C10-M': {
    weapon: { reload: 1.0, barrels: [-20, 0, 20].map((a, i) => b({ angle: a, width: 0.75, flare: 0.35, pellets: 6, spread: 14, damage: 0.48, life: 0.5, delay: i * 0.1 })) },
  },
  'C10-L': {
    weapon: { reload: 1.4, burst: { count: 2, interval: 0.12 }, barrels: [b({ width: 1.2, flare: 0.9, pellets: 16, spread: 34, damage: 0.36, life: 0.55, speed: 0.9, recoil: 2.6 })] },
  },

  // ── C11 Rebond ─────────────────────────────────────────────
  'C11-M': {
    weapon: { reload: 0.6, barrels: [-30, 0, 30].map((a, i) => b({ angle: a, width: 0.6, damage: 0.85, life: 2.6, delay: i / 3, proj: { bounces: 2, bounceLoss: 0.3 } })) },
  },
  'C11-L': {
    weapon: {
      reload: 0.55,
      barrels: [-20, 20, 160, -160].map((a, i) => b({ angle: a, width: 0.55, damage: 0.95, speed: 0.6, life: 3.2, pen: 1.3, delay: i / 4, proj: { bounces: 3, bounceLoss: 0.2, maxActive: 20 } })),
    },
  },

  // ── C12 Tourelles ──────────────────────────────────────────
  'C12-M': {
    weapon: { reload: 0.8, turnRate: 3, barrels: [b({ width: 1.0, damage: 2.0, pen: 1.3 }), ...[0, 120, 240].map((a) => autoTurret({ angle: a, damage: 0.5 }))] },
    body: { speed: 0.92 },
  },
  'C12-L': {
    weapon: {
      reload: 1.0,
      turnRate: 2.6,
      barrels: [
        b({ offset: -0.4, width: 0.85, damage: 2.0, pen: 1.4 }),
        b({ offset: 0.4, width: 0.85, damage: 2.0, pen: 1.4, delay: 0.5 }),
        ...[45, 135, 225, 315].map((a) => autoTurret({ angle: a, damage: 0.5 })),
      ],
    },
    body: { speed: 0.85, health: 1.1 },
  },

  // ── C13 Énergie ────────────────────────────────────────────
  'C13-M': {
    weapon: { reload: 0.4, barrels: [-0.3, 0.3].map((o, i) => emitter({ offset: o, width: 0.5, damage: 0.95, life: 0.17, delay: i * 0.5, proj: beam({ chain: { jumps: 3, range: 240, falloff: 0.6 } }) })) },
  },
  'C13-L': {
    weapon: {
      reload: 1.5,
      barrels: [b({ look: 'emitter', width: 1.2, damage: 2.4, speed: 0.5, life: 1.6, pen: 2.5, size: 1.5, proj: orb({ homing: 1, pull: { radius: 180, force: 160 }, explode: { radius: 170, damage: 1.4, onExpire: true, onHit: true } }) })],
    },
  },

  // ── C14 Contrôle ───────────────────────────────────────────
  'C14-M': {
    weapon: {
      reload: 0.7,
      barrels: [b({ offset: -0.35, width: 0.75, damage: 0.55, proj: { slow: { mul: 0.65, dur: 1.4 } } }), b({ offset: 0.35, width: 0.75, damage: 0.55, delay: 0.5, proj: { knock: 200 } })],
    },
  },
  'C14-L': {
    weapon: {
      reload: 1.4,
      barrels: [
        b({ look: 'emitter', width: 1.1, damage: 1.4, speed: 0.6, life: 1.3, size: 1.4, proj: orb({ pull: { radius: 230, force: 240 }, explode: { radius: 220, damage: 0.8, onExpire: true, onHit: true } }) }),
        b({ angle: -30, offset: -0.5, width: 0.5, damage: 0.6, delay: 0.25, proj: { slow: { mul: 0.7, dur: 1.2 } } }),
        b({ angle: 30, offset: 0.5, width: 0.5, damage: 0.6, delay: 0.75, proj: { slow: { mul: 0.7, dur: 1.2 } } }),
      ],
    },
  },

  // ── C15 Hybrides ───────────────────────────────────────────
  'C15-M': {
    weapon: {
      reload: 0.85,
      barrels: [
        b({ width: 0.95, damage: 1.6 }),
        hatch(90, { damage: 0.75, proj: drone(3) }),
        hatch(-90, { damage: 0.75, delay: 0.5, proj: drone(3) }),
        b({ look: 'mine', angle: 180, length: 1.3, width: 0.8, speed: 0.4, damage: 1.5, pen: 0.5, proj: mine(4, 70, 1) }),
      ],
    },
  },
  'C15-L': {
    weapon: {
      reload: 0.9,
      barrels: [
        emitter({ offset: -0.3, width: 0.5, damage: 0.7, proj: beam() }),
        b({ offset: 0.3, width: 0.6, damage: 0.9, delay: 0.5 }),
        mortar({ width: 0.8, length: 1.4, damage: 1.2, delay: 0.25, proj: shell(200, 800, 80) }),
        hatch(90, { damage: 0.6, proj: drone(2) }),
        hatch(-90, { damage: 0.6, delay: 0.5, proj: drone(2) }),
      ],
    },
    body: { health: 0.9 },
  },
};
